import { makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore';
import { FilterStore } from './filterStore';
import { RootStore } from './rootStore';
import { UserStore } from './userStore';
import { ApiError } from '/src/api';
import { AlertService } from '/src/api/services/alert-service';
import { Alert, TAlertId, TCompanyId, TLenderId, TLoanId } from '/src/lib/models';
import { AlertStatus, DataModelPluralType } from '/src/lib/types';

export interface AlertStoreProps {
  /**
   * List of Alerts
   */
  alerts?: Alert[];
  /**
   * Number of unread Alerts
   */
  newAlertCount: number;
  /**
   * Whether to poll the API for new Alerts
   */
  allowPoll: boolean;
  /**
   * How often to poll for new Alerts. Value is in milliseconds. Defaults to 10 minutes
   */
  pollTimer: number;
}

export class AlertStore extends BaseStore implements AlertStoreProps {
  filterStore: FilterStore;
  userStore: UserStore;

  alerts?: Alert[];
  newAlertCount = 0;
  allowPoll = true; // Default is to let the app poll for new updates every 10 minutes
  pollTimer = 10 * 60 * 1000; // ms

  constructor(rootStore: RootStore) {
    super(rootStore);

    this.filterStore = rootStore.filterStore;
    this.userStore = rootStore.userStore;

    makeObservable(this, {
      alerts: observable,
      newAlertCount: observable,
      allowPoll: observable,
      pollTimer: observable,
    });
  }

  /**** Data Processing ****/

  /**
   * Prepares Alert responses.
   *
   * @param alert - Alert to process
   * @returns The prepared Alert
   */
  _interceptor = (alert: Alert) => {
    if (alert.asset) {
      alert.source = {
        __t: DataModelPluralType.Asset,
        _id: alert.asset._id,
        company: alert.loan?.borrower,
        loan: alert.loan,
        // loan: alert.loan!._id, // <-- original
      };
      delete alert.loan;
      delete alert.asset;
    }
    return alert;
  };

  /**** Alert CRUD ****/

  /**
   * Fetch Alerts
   */
  query = async () => {
    if (!this.rootStore.userStore.user) return;

    try {
      const alerts = (await AlertService.query()).map(this._interceptor).filter((alert) => !!alert.source);

      runInAction(() => {
        this.alerts = alerts;
        this.newAlertCount = alerts.filter((a) => a.status === AlertStatus.Unread).length;
      });
    } catch (err) {
      if ((err as ApiError).status === 401) {
        const errBody = (err as ApiError).body as Record<string, any>;
        if (errBody.message?.toLocaleLowerCase() === 'user is not logged in') {
          this.rootStore.userStore.signout();
        }
      } else {
        console.error(err);
      }
    }
  };

  /**
   * Get Alert
   */
  get = async (id: TAlertId) => {
    return this._interceptor(await AlertService.get(id));
  };

  /**
   * Update Alert
   */
  update = async (alert: Alert) => {
    return await AlertService.update(alert._id, alert);
  };

  /**
   * Looks up names in the Companies list
   */
  getCompanyName = (companyId?: TCompanyId) => {
    if (!companyId) return;
    const companies = this.filterStore.companyListGet();
    return companies?.find((company) => company._id === companyId)?.name;
  };

  /**
   * Looks up names in the Loans list
   */
  getLoanName = (loanId?: TLoanId) => {
    if (!loanId) return;
    const loans = this.filterStore.loanListGet();
    return loans?.find((loan) => loan._id === loanId)?.name;
  };

  /**
   * Looks up names in the Lenders list
   */
  getLenderName = (lenderId?: TLenderId) => {
    if (!lenderId) return;
    const lenders = this.filterStore.lenderListGet();
    return Array.isArray(lenders) ? lenders.find((lender) => lender._id === lenderId)?.name : lenders;
  };

  /**
   * Counts the unread alerts in a given array
   */
  scanNewAlerts = (alerts: Alert[]) => {
    return alerts.filter((alert) => alert.status === AlertStatus.Unread).length;
  };

  /**
   * Marks all unread Alerts as read
   */
  markAsRead = async () => {
    if (!this.alerts) return;

    const updateRequests = this.alerts
      ?.filter((alert) => alert.status === AlertStatus.Unread)
      .map((alert) => {
        alert.status = AlertStatus.Read;
        // if (alert.source) delete alert.source;
        return AlertService.update(alert._id, alert);
      });

    await Promise.all(updateRequests);

    await this.query();
  };

  /**
   * Poll the server for new alerts
   */
  tick = async () => {
    if (!this.allowPoll || !this.userStore.isAdmin) return;

    const alerts = await AlertService.query();

    runInAction(() => {
      this.alerts = alerts;
      this.newAlertCount = this.scanNewAlerts(alerts);
    });
  };
}
